import { useState, useEffect, useRef, useCallback } from 'react';

import * as SERVICE from '../../../../constants/service.types';
import { ROOT_GROUP_ID } from '../../../../constants/group';
import {
  parseResponseBody,
  prepareBody,
  requestSocketPromiseWithTimeout,
} from '../../../../socket/query';

/**
 *
 * @returns {Promise<{
 *  parentDeviceGroupId: number
 * deviceGroups: CameraDeviceGroup[] }>}
 */
export const getCameraGroupsByParentId = async ({
  namespace,
  parentDeviceGroupId,
}) => {
  const response = await requestSocketPromiseWithTimeout({
    appServiceType: SERVICE.LIST_DEVICE_GROUP_BY_PARENT_DEVICE_GROUP,
    body: prepareBody({
      namespace,
      parentDeviceGroupId,
    }),
  });

  return parseResponseBody(response);
};

/**
 * 
 * @param {*} param0 
 * @returns {Promise<{
 *  deviceGroup: CameraDeviceGroup & {
 *   devices: Camera[]
 *  }
 *  deviceGroupId: number
 *  total: number
 *  page: number
 *  size: number
  timeRequest: number }>}
 */
export const getDeviceByGroupId = async ({
  // page,
  // size,
  namespace,
  deviceGroupId,
  timeRequest,
}) => {
  const response = await requestSocketPromiseWithTimeout({
    appServiceType: SERVICE.LIST_DEVICE_BY_DEVICE_GROUP_ID,
    body: prepareBody({
      namespace,
      deviceGroupId,
      timeRequest,
    }),
  });

  console.log('LIST_DEVICE_BY_DEVICE_GROUP_ID:', parseResponseBody(response));
  return parseResponseBody(response);
};

/**
 * Data structure:
 *  Group map: GroupId -> Group // lookup by id
 *  Tree map: GroupId -> [groupId,...] // relationship in flat object
 *  Camera map: { cameraSerial: camera } // cameras of each group
 *  Group to camera map: GroupId -> [cameraSerial,...] // cameras of each group
 *  Get children:
 *   (1)If: treemap has children:
 *     GroupId -> children -> GroupMap[childrenId][]
 *   Else:
 *     fetch children -> assign to group map, assign to treemap -> go to 1
 */ export const useCameraGroups = ({ namespace }) => {
  const groupMap = useRef({});
  const treeMap = useRef({});
  const cameraMap = useRef({});
  const groupToCameraMap = useRef({});
  const dirtyGroup = useRef({});
  const [data, setData] = useState();
  /**
   * Use to force update data
   */
  const [updateIndex, setUpdateIndex] = useState(0);
  const notifyUpdate = useCallback(
    () => setUpdateIndex((prev) => prev + 1),
    [],
  );

  useEffect(() => {
    getCameraGroupsByParentId({
      namespace,
      parentDeviceGroupId: ROOT_GROUP_ID,
    }).then(setData);
  }, [namespace]);

  useEffect(() => {
    if (data?.deviceGroups) {
      data.deviceGroups.forEach((group) => {
        groupMap.current[group.id] = group;
        treeMap.current[group.parentDeviceGroupId] =
          treeMap.current[group.parentDeviceGroupId] || [];
        treeMap.current[group.parentDeviceGroupId].push(group);
      });
      notifyUpdate();
    }
  }, [data, notifyUpdate]);

  const markDirty = useCallback(
    (groupId) => {
      console.log('MARK DIRTY', groupId);
      if (treeMap.current[groupId]) {
        dirtyGroup.current[groupId] = true;
        notifyUpdate();
      }
    },
    [notifyUpdate],
  );

  const getChildren = useCallback(
    (groupId) => {
      console.log('TREE MAP', treeMap.current, treeMap.current[groupId]);
      if (treeMap.current[groupId] && !dirtyGroup.current[groupId]) {
        dirtyGroup.current[groupId] = false;

        return Promise.resolve(
          treeMap.current[groupId].map((id) => groupMap.current[id]),
        );
      }

      console.log('GET CHILDREN', groupId);

      return getCameraGroupsByParentId({
        namespace,
        parentDeviceGroupId: groupId,
      }).then((subgroupData) => {
        treeMap.current[groupId] = subgroupData.deviceGroups.map(
          (child) => child.id,
        );

        subgroupData.deviceGroups.forEach((child) => {
          groupMap.current[child.id] = child;
        });

        return treeMap.current[groupId].map((id) => groupMap.current[id]);
      });
    },
    [namespace, updateIndex],
  );

  const getCameras = useCallback(
    (groupId) => {
      if (groupToCameraMap.current[groupId] && !dirtyGroup.current[groupId]) {
        dirtyGroup.current[groupId] = false;

        return Promise.resolve(
          groupToCameraMap.current[groupId].map(
            (serial) => cameraMap.current[serial],
          ),
        );
      }

      return getDeviceByGroupId({
        namespace,
        deviceGroupId: groupId,
      }).then(({ cameraInfos }) => {
        const devices = cameraInfos || [];
        devices.forEach((device) => {
          cameraMap.current[device.serial] = device;
        });
        groupToCameraMap.current[groupId] = devices.map(
          (device) => device.serial,
        );

        return groupToCameraMap.current[groupId].map(
          (serial) => cameraMap.current[serial],
        );
      });
    },
    [namespace, updateIndex],
  );

  const getCamera = useCallback(
    (serial) => {
      return cameraMap.current[serial];
    },
    [cameraMap, updateIndex],
  );

  const rootGroups = Object.values(groupMap.current).filter(
    (group) => group.parentDeviceGroupId === ROOT_GROUP_ID,
  );

  const addGroup = useCallback(
    (group) => {
      groupMap.current[group.id] = group;
      treeMap.current[group.parentDeviceGroupId] =
        treeMap.current[group.parentDeviceGroupId] || [];
      treeMap.current[group.parentDeviceGroupId].push(group.id);
      groupToCameraMap.current[group.id] = [];
      notifyUpdate();
      console.log('addGroup', group, groupMap.current, treeMap.current);
    },
    [groupMap, treeMap, groupToCameraMap, notifyUpdate],
  );

  const removeGroup = useCallback(
    (groupId) => {
      delete groupMap.current[groupId];
      delete treeMap.current[groupId];
      delete groupToCameraMap.current[groupId];
      notifyUpdate();
    },
    [groupMap, treeMap, groupToCameraMap, notifyUpdate],
  );

  const removeCamera = useCallback(
    (groupId, serial) => {
      if (groupToCameraMap.current[groupId]) {
        groupToCameraMap.current[groupId] = groupToCameraMap.current[
          groupId
        ].filter((cameraSerial) => cameraSerial !== serial);

        notifyUpdate();
      }
    },
    [groupToCameraMap, notifyUpdate],
  );

  const renameGroup = useCallback(
    (groupId, name) => {
      if (groupMap.current[groupId]) {
        groupMap.current[groupId].name = name;
        notifyUpdate();
      }
    },
    [groupMap, notifyUpdate],
  );

  const updateCamera = useCallback(
    (serial, data) => {
      if (cameraMap.current[serial]) {
        cameraMap.current[serial] = { ...cameraMap.current[serial], ...data };
        notifyUpdate();
      }
    },
    [cameraMap, notifyUpdate],
  );

  const addCamera = useCallback(
    (groupId, camera, notify = true) => {
      if (!cameraMap.current[camera.serial]) {
        cameraMap.current[camera.serial] = camera;
      }

      if (
        groupToCameraMap.current[groupId] &&
        !groupToCameraMap.current[groupId].includes(camera.serial)
      ) {
        groupToCameraMap.current[groupId].push(camera.serial);
        if (notify) {
          notifyUpdate();
        }
      }
    },
    [groupToCameraMap, notifyUpdate],
  );

  const addCameras = useCallback(
    (groupId, cameras) => {
      cameras.forEach((camera) => addCamera(groupId, camera, false));
      notifyUpdate();
    },
    [addCamera, notifyUpdate],
  );

  return {
    rootGroups,
    getChildren,
    getCameras,
    groupMap,
    treeMap,
    cameraMap,
    addGroup,
    removeGroup,
    removeCamera,
    renameGroup,
    updateCamera,
    getCamera,
    addCamera,
    addCameras,
    markDirty,
  };
};
